import React, { useState } from 'react';
import {
  Hammer,
  Building,
  ShieldCheck,
  CheckCircle2,
  Clock,
  Wrench,
  Search,
  Plus,
  Phone,
  FileCheck2,
} from 'lucide-react';
import { DefectWarrantyClaim } from '../../types';
import { INITIAL_DEFECT_CLAIMS } from '../../data/adminModulesData';

export const AdminDeveloperView: React.FC = () => {
  const [claims, setClaims] = useState<DefectWarrantyClaim[]>(INITIAL_DEFECT_CLAIMS);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // New claim form
  const [newHouse, setNewHouse] = useState('');
  const [newResident, setNewResident] = useState('');
  const [newCategory, setNewCategory] = useState<DefectWarrantyClaim['category']>('roof_leak');
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const filteredClaims = claims.filter(
    (c) =>
      c.house_number.includes(searchQuery) ||
      c.resident_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddClaim = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newHouse || !newTitle) return;

    const newClaim: DefectWarrantyClaim = {
      id: `def-${Date.now()}`,
      claim_number: `DEF-2569-${Math.floor(100 + Math.random() * 900)}`,
      house_number: newHouse,
      resident_name: newResident || 'เจ้าของบ้าน',
      category: newCategory,
      title: newTitle,
      description: newDesc,
      developer_contractor: 'นราวดี กรุ๊ป (ศูนย์บริการหลังการขาย)',
      warranty_status: 'under_warranty',
      status: 'inspection_scheduled',
      created_at: new Date().toISOString(),
      scheduled_date: new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
      dev_engineer_name: 'วิศวกรบริการ นราวดี',
    };

    setClaims([newClaim, ...claims]);
    setShowAddModal(false);
    setNewHouse('');
    setNewResident('');
    setNewTitle('');
    setNewDesc('');
  };

  const updateClaimStatus = (id: string, newStatus: DefectWarrantyClaim['status']) => {
    setClaims((prev) =>
      prev.map((c) => (c.id === id ? { ...c, status: newStatus } : c))
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-orange-50 text-orange-600 border border-orange-200">
            <Hammer className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              10. สำหรับผู้พัฒนาโครงการ & การรับประกัน Defect (Developer Liaison & Warranty)
            </h2>
            <p className="text-xs text-slate-500">
              ผู้พัฒนาโครงการ: บริษัท นราวดี พร็อพเพอร์ตี้ จำกัด • ประสานงานเคลมประกันบ้านและส่งมอบสาธารณูปโภค
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
        >
          <Plus className="w-4 h-4" /> ส่งเรื่องเคลมประกัน Defect
        </button>
      </div>

      {/* Handover Status Box */}
      <div className="bg-slate-900 text-white p-5 rounded-2xl shadow-md space-y-3">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              สถานะการส่งมอบสาธารณูปโภค
            </span>
            <h3 className="font-bold text-base mt-1">ส่งมอบพื้นที่ส่วนกลางและบัญชีกองทุนสมบูรณ์ 100%</h3>
          </div>
          <div className="text-right text-xs text-slate-300">
            <span>สายด่วนฝ่ายช่างผู้พัฒนา: </span>
            <strong className="text-white font-mono text-sm">035-245-999</strong>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 text-xs">
          <div className="p-2.5 bg-slate-800/80 rounded-xl border border-slate-700">
            <span className="text-slate-400 block text-[11px]">ถนนเมน & ซอย</span>
            <span className="font-bold text-emerald-400">ตรวจรับมอบ 100%</span>
          </div>
          <div className="p-2.5 bg-slate-800/80 rounded-xl border border-slate-700">
            <span className="text-slate-400 block text-[11px]">ระบบบำบัดน้ำ & ท่อ</span>
            <span className="font-bold text-emerald-400">ตรวจรับมอบ 100%</span>
          </div>
          <div className="p-2.5 bg-slate-800/80 rounded-xl border border-slate-700">
            <span className="text-slate-400 block text-[11px]">คลับเฮาส์ & สระว่ายน้ำ</span>
            <span className="font-bold text-emerald-400">ส่งมอบสมบูรณ์</span>
          </div>
          <div className="p-2.5 bg-slate-800/80 rounded-xl border border-slate-700">
            <span className="text-slate-400 block text-[11px]">เงินกองทุนจัดตั้งนิติ</span>
            <span className="font-bold text-emerald-400">โอนเข้าบัญชีนิติครบถ้วน</span>
          </div>
        </div>
      </div>

      {/* Defect Claims List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3 bg-slate-50/50">
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="ค้นหาเลขเคลม, บ้านเลขที่..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-white"
            />
          </div>
          <span className="text-xs text-slate-500">รายการเคลมทั้งหมด {claims.length} เรื่อง</span>
        </div>

        <div className="divide-y divide-slate-100">
          {filteredClaims.map((claim) => (
            <div key={claim.id} className="p-5 hover:bg-slate-50/60 transition-colors space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono bg-slate-100 text-slate-700 border border-slate-200">
                    {claim.claim_number}
                  </span>
                  <span className="font-bold text-slate-900 text-sm">
                    บ้าน {claim.house_number} ({claim.resident_name})
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                    {claim.category === 'roof_leak' && 'หลังคารั่วซึม'}
                    {claim.category === 'paint_finishing' && 'งานสี & ผนัง'}
                    {claim.category === 'plumbing' && 'ระบบท่อ'}
                    {claim.category === 'common_infra' && 'สาธารณูปโภค'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                      claim.status === 'resident_signed_off'
                        ? 'bg-emerald-100 text-emerald-800'
                        : claim.status === 'fixing'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {claim.status === 'resident_signed_off' && '✅ ลูกบ้านตรวจรับงานแล้ว'}
                    {claim.status === 'fixing' && '🔧 กำลังเข้าซ่อมแซม'}
                    {claim.status === 'inspection_scheduled' && '📅 นัดตรวจหน้างาน'}
                    {claim.status === 'received_by_dev' && '📥 ผู้พัฒนารับเรื่อง'}
                  </span>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 text-sm">{claim.title}</h4>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed bg-slate-50 p-3 rounded-xl">
                  {claim.description}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs pt-1">
                <div className="text-slate-500">
                  <span>ผู้รับผิดชอบ: <strong className="text-slate-700">{claim.dev_engineer_name}</strong></span>
                  {claim.scheduled_date && (
                    <span className="ml-3 text-blue-700 font-medium">วันนัดหมาย: {claim.scheduled_date}</span>
                  )}
                </div>

                <select
                  value={claim.status}
                  onChange={(e) => updateClaimStatus(claim.id, e.target.value as any)}
                  className="text-xs p-1.5 rounded-lg border border-slate-200 bg-white font-medium text-slate-700 cursor-pointer"
                >
                  <option value="received_by_dev">ผู้พัฒนารับเรื่อง</option>
                  <option value="inspection_scheduled">นัดตรวจหน้างาน</option>
                  <option value="fixing">กำลังเข้าซ่อม</option>
                  <option value="resident_signed_off">ลูกบ้านเซ็นรับงานเสร็จ</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Claim Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="font-bold text-base">ส่งเรื่องเคลมประกัน Defect กับผู้พัฒนาโครงการ</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <form onSubmit={handleAddClaim} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">บ้านเลขที่ *</label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น 88/45"
                    value={newHouse}
                    onChange={(e) => setNewHouse(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ชื่อลูกบ้าน</label>
                  <input
                    type="text"
                    placeholder="ชื่อ-นามสกุล"
                    value={newResident}
                    onChange={(e) => setNewResident(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">หมวดหมู่งาน Defect</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as any)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                >
                  <option value="roof_leak">หลังคารั่วซึม / รางน้ำ</option>
                  <option value="paint_finishing">งานสี / รอยแตกลายงา</option>
                  <option value="plumbing">ระบบประปา / ท่อน้ำทิ้ง</option>
                  <option value="electrical">ระบบไฟฟ้า / สวิตช์</option>
                  <option value="structure">โครงสร้าง / พื้นทรุด</option>
                </select>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">หัวข้อการแจ้งเคลม *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น น้ำซึมฝ้าเพดานห้องนอนใหญ่"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">รายละเอียดปัญหา</label>
                <textarea
                  rows={3}
                  placeholder="ระบุจุดที่พบปัญหา และเวลาที่สะดวกให้ช่างเข้าตรวจสอบ..."
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold"
                >
                  ส่งเรื่องเคลม
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
